import React from 'react';
import { Helmet } from 'react-helmet-async';
import { generateJsonLd, createWebsiteLd } from '../utils/schema';
import { useLocation } from 'react-router-dom';
import type { JsonLdSchema } from '../types/schema';

export interface SEOProps {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageAlt?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterImageAlt?: string;
  linkedinTitle?: string;
  linkedinDescription?: string;
  keywords?: string;
  lang?: string;
  schema?: JsonLdSchema;
  type?: 'website' | 'profile';
}

const SEO: React.FC<SEOProps> = ({
  title = 'Belgian Game Mentors | Expert Mentorship for Game Industry Professionals',
  description = 'GameMentors.be connects game industry professionals with expert mentors. Learn from seasoned professionals who can help grow your game studio.',
  ogTitle = 'Belgian Game Mentors | Find Game Industry Mentors',
  ogDescription = 'Unlock the potential of your game studio with the help of expert mentors from GameMentors.be. Connect with industry veterans ready to share their experience.',
  ogImage = 'https://gamementors.be/assets/social.png',
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogImageAlt = 'GameMentors - Expert Game Industry Mentorship',
  twitterTitle = 'Belgian Game Mentors | Expert Mentorship for Game Industry Professionals',
  twitterDescription = 'Connect with expert mentors from the game industry and unlock the potential of your game studio. Learn from mentors with years of hands-on experience.',
  twitterImage = 'https://gamementors.be/assets/social.png',
  twitterImageAlt = 'GameMentors - Expert Game Industry Mentorship',
  linkedinTitle = 'Belgian Game Mentors on LinkedIn',
  linkedinDescription = 'Follow Belgian Game Mentors on LinkedIn for the latest insights and expert mentoring in the gaming industry.',
  keywords = 'game mentors, VAF, Flega, Walga, Flanders Game Hub, belgian, belgium, game industry, mentorship program, game development, video games, game developers, game mentorship, game industry professionals',
  lang = 'en',
  schema,
  type = 'website',
}) => {
  // Generate default schema if none provided
  const defaultSchema = createWebsiteLd();
  const jsonLdString = generateJsonLd(schema || defaultSchema);

  const baseUrl = 'https://gamementors.be';
  const location = useLocation();
  const currentUrl = `${baseUrl}${location.pathname}${location.search}`;

  return (
    <Helmet htmlAttributes={{ lang }}>
      {/* Character Set */}
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />

      {/* Stylesheet */}
      <link href={`${baseUrl}/output.css`} rel="stylesheet" />

      {/* Title */}
      <title>{title}</title>

      {/* Meta Description */}
      <meta name="description" content={description} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={ogImageWidth.toString()} />
      <meta property="og:image:height" content={ogImageHeight.toString()} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:site_name" content="GameMentors" />
      <meta property="og:profile" content="https://linkedin.com/company/gamementors" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={twitterImage} />
      <meta name="twitter:image:alt" content={twitterImageAlt} />
      <meta name="twitter:site" content="@ericdiepeveen" />
      <meta name="twitter:creator" content="@ericdiepeveen" />

      {/* LinkedIn */}
      <meta property="og:url" content="https://www.linkedin.com/company/gamementors" />
      <meta property="og:title" content={linkedinTitle} />
      <meta property="og:description" content={linkedinDescription} />
      <meta property="og:image" content={ogImage} />

      {/* Canonical */}
      <link rel="canonical" href={currentUrl} />

      {/* Favicon */}
      <link rel="icon" href={`${baseUrl}/assets/icon.webp`} type="image/webp" />
      <link rel="shortcut icon" href={`${baseUrl}/assets/icon.webp`} type="image/webp" />
      <link rel="apple-touch-icon" href={`${baseUrl}/assets/icon.webp`} />

      {/* SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="author" content="Belgian Game Mentors" />
      <meta name="keywords" content={keywords} />

      {/* Additional SEO */}
      <meta name="theme-color" content="#ffffff" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="GameMentors" />

      {/* JSON-LD */}
      <script type="application/ld+json">{jsonLdString}</script>
    </Helmet>
  );
};

export default SEO;
